import React, { useState, useEffect } from 'react';
import {
  EnvelopeIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
  ChartBarIcon,
  DocumentDuplicateIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import Card, { CardContent } from '../../../components/ui/Card';

interface EmailForm {
  id: string;
  title: string;
  description: string;
  buttonText: string;
  successMessage: string;
  redirectUrl?: string;
  isActive: boolean;
  createdAt: string;
  subscriberCount: number;
  embedCode?: string;
}

/**
 * Email Capture Forms Manager
 * Allows creators to create and manage lead capture forms
 */
const EmailCapturePage: React.FC = () => {
  const [forms, setForms] = useState<EmailForm[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState<EmailForm | null>(null);
  const [editingForm, setEditingForm] = useState<Partial<EmailForm> | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Mock data for demo
  useEffect(() => {
    const mockForms: EmailForm[] = [
      {
        id: '1',
        title: 'Join My Newsletter',
        description: 'Get exclusive updates and early access to new content!',
        buttonText: 'Subscribe Now',
        successMessage: 'Thanks for subscribing! Check your email to confirm.',
        isActive: true,
        createdAt: '2024-01-15',
        subscriberCount: 234,
      },
      {
        id: '2',
        title: 'Early Access Signup',
        description: 'Be the first to know when new tiers become available.',
        buttonText: 'Get Early Access',
        successMessage: "You're on the list! We'll notify you soon.",
        redirectUrl: 'https://example.com/thank-you',
        isActive: true,
        createdAt: '2024-02-20',
        subscriberCount: 89,
      },
    ];
    setForms(mockForms);
  }, []);

  const handleCreateForm = () => {
    setEditingForm({
      title: '',
      description: '',
      buttonText: 'Subscribe',
      successMessage: 'Thank you for subscribing!',
      isActive: true,
    });
    setShowCreateModal(true);
  };

  const handleEditForm = (form: EmailForm) => {
    setEditingForm({ ...form });
    setShowCreateModal(true);
  };

  const handleSaveForm = () => {
    if (!editingForm?.title || !editingForm?.description) {
      alert('Please fill in all required fields');
      return;
    }

    if (editingForm.id) {
      // Update existing
      setForms(forms.map((f) => (f.id === editingForm.id ? { ...f, ...editingForm } as EmailForm : f)));
    } else {
      // Create new
      const newForm: EmailForm = {
        id: Date.now().toString(),
        title: editingForm.title,
        description: editingForm.description,
        buttonText: editingForm.buttonText || 'Subscribe',
        successMessage: editingForm.successMessage || 'Thank you!',
        redirectUrl: editingForm.redirectUrl,
        isActive: editingForm.isActive ?? true,
        createdAt: new Date().toISOString().split('T')[0],
        subscriberCount: 0,
      };
      setForms([...forms, newForm]);
    }

    setShowCreateModal(false);
    setEditingForm(null);
  };

  const handleDeleteForm = (id: string) => {
    if (confirm('Are you sure you want to delete this form? This cannot be undone.')) {
      setForms(forms.filter((f) => f.id !== id));
    }
  };

  const handleToggleActive = (id: string) => {
    setForms(
      forms.map((f) => (f.id === id ? { ...f, isActive: !f.isActive } : f))
    );
  };

  const handlePreview = (form: EmailForm) => {
    setSelectedForm(form);
    setShowPreviewModal(true);
  };

  const generateEmbedCode = (formId: string) => {
    return `<div data-patreon-form="${formId}"></div>
<script src="https://yourdomain.com/embed.js"></script>`;
  };

  const handleCopyEmbed = (formId: string) => {
    const embedCode = generateEmbedCode(formId);
    navigator.clipboard.writeText(embedCode);
    setCopiedId(formId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Email Capture Forms</h1>
          <p className="text-gray-600 mt-1">Create forms to grow your email list</p>
        </div>
        <button
          onClick={handleCreateForm}
          className="flex items-center gap-2 px-4 py-2 bg-patreon-orange text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          Create Form
        </button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Forms</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{forms.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <EnvelopeIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Forms</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {forms.filter((f) => f.isActive).length}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Subscribers</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {forms.reduce((sum, f) => sum + f.subscriberCount, 0)}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <ChartBarIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Forms List */}
      <div className="space-y-4">
        {forms.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <EnvelopeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No forms yet</h3>
              <p className="text-gray-600 mb-4">Create your first email capture form to start growing your list</p>
              <button
                onClick={handleCreateForm}
                className="inline-flex items-center gap-2 px-4 py-2 bg-patreon-orange text-white rounded-lg hover:bg-orange-600"
              >
                <PlusIcon className="w-5 h-5" />
                Create First Form
              </button>
            </CardContent>
          </Card>
        ) : (
          forms.map((form) => (
            <Card key={form.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{form.title}</h3>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          form.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {form.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4">{form.description}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Created: {form.createdAt}</span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <ChartBarIcon className="w-4 h-4" />
                        {form.subscriberCount} subscribers
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handlePreview(form)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Preview"
                    >
                      <EyeIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleCopyEmbed(form.id)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Copy Embed Code"
                    >
                      {copiedId === form.id ? (
                        <CheckIcon className="w-5 h-5 text-green-600" />
                      ) : (
                        <DocumentDuplicateIcon className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEditForm(form)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleToggleActive(form.id)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        form.isActive
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {form.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleDeleteForm(form.id)}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingForm?.id ? 'Edit Form' : 'Create New Form'}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Form Title *
                </label>
                <input
                  type="text"
                  value={editingForm?.title || ''}
                  onChange={(e) => setEditingForm({ ...editingForm, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                  placeholder="Join My Newsletter"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  value={editingForm?.description || ''}
                  onChange={(e) => setEditingForm({ ...editingForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                  placeholder="Get exclusive updates and early access..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Button Text
                </label>
                <input
                  type="text"
                  value={editingForm?.buttonText || ''}
                  onChange={(e) => setEditingForm({ ...editingForm, buttonText: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                  placeholder="Subscribe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Success Message
                </label>
                <input
                  type="text"
                  value={editingForm?.successMessage || ''}
                  onChange={(e) =>
                    setEditingForm({ ...editingForm, successMessage: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                  placeholder="Thank you for subscribing!"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Redirect URL (optional)
                </label>
                <input
                  type="url"
                  value={editingForm?.redirectUrl || ''}
                  onChange={(e) => setEditingForm({ ...editingForm, redirectUrl: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
                  placeholder="https://example.com/thank-you"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={editingForm?.isActive ?? true}
                  onChange={(e) => setEditingForm({ ...editingForm, isActive: e.target.checked })}
                  className="w-4 h-4 text-patreon-orange border-gray-300 rounded focus:ring-patreon-orange"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                  Make form active immediately
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingForm(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveForm}
                className="px-4 py-2 bg-patreon-orange text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                {editingForm?.id ? 'Save Changes' : 'Create Form'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Form Preview</h2>
            </div>

            <div className="p-8">
              {/* Preview of the actual form */}
              <div className="border-2 border-gray-200 rounded-lg p-6 bg-gray-50">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{selectedForm.title}</h3>
                <p className="text-gray-600 mb-6">{selectedForm.description}</p>
                <div className="flex gap-3">
                  <input
                    type="email"
                    placeholder="Your email address"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    disabled
                  />
                  <button className="px-6 py-2 bg-patreon-orange text-white rounded-lg font-medium">
                    {selectedForm.buttonText}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailCapturePage;
